import { FuelAlertVariant } from "@deere/fuel-react";
import {
  Assignment,
  Cancel,
  CheckCircleOutline,
  DirectionsRun,
  LocalShipping,
  MonetizationOn,
  SvgIconComponent,
  TimerOutlined,
  Warning,
} from "@mui/icons-material";
import { AddressType } from "../../../api/returns/returnsInterfaces";
import { convertToCamelCase } from "../../../utils/formatters";

export const dayJSLang: { [key: string]: string } = {
  en_US: "en",
  pt_BR: "pt-br",
  es_MX: "es-mx",
  zh_CN: "zh-cn",
  fr_CA: "fr-ca",
  de_DE: "de-de",
  it_IT: "it-it",
  pl_PL: "pl-pl",
};

// Deprecated
export const dateFormat: { [key: string]: string } = {
  en_US: "MMM DD, YYYY",
  pt_BR: "DD MMM. YYYY",
  es_MX: "DD MMM YYYY",
  zh_CN: "MMM DD, YYYY",
  fr_CA: "MMM DD, YYYY",
  de_DE: "DD MMM YYYY",
  it_IT: "DD MMM YYYY",
  pl_PL: "DD MMM YYYY",
};

export const commonDateFormat = "DD-MMM-YYYY";

export type IconType = {
  icon: SvgIconComponent;
  variant: FuelAlertVariant;
  type: "default" | "bold";
  name: string;
};

export type StatusTexts = {
  inProcess: string;
  needAction: string;
  waitingForNFe: string;
  waitingOnSupplier: string;
  readyToShip: string;
  rejected: string;
  completed: string;
  dealerPreparation: string;
  scrap: string;
};

export type TypeOfPartsTexts = {
  SMALL: string;
  BULK: string;
  UNKNOWN: string;
};

export const getStatusIconInfo = (
  statusText: StatusTexts
): { [key: string]: IconType } => {
  const base: { [key: string]: IconType } = {
    needAction: {
      icon: Warning,
      variant: "warning",
      type: "default",
      name: statusText.needAction,
    },
    inProcess: {
      icon: DirectionsRun,
      variant: "info",
      type: "default",
      name: statusText.inProcess,
    },
    readyToShip: {
      icon: LocalShipping,
      variant: "success",
      type: "default",
      name: statusText.readyToShip,
    },
    rejected: {
      icon: Cancel,
      variant: "error",
      type: "default",
      name: statusText.rejected,
    },
    completed: {
      icon: CheckCircleOutline,
      variant: "success",
      type: "bold",
      name: statusText.completed,
    },
    waitingForNFe: {
      icon: TimerOutlined,
      variant: "warning",
      type: "default",
      name: statusText.waitingForNFe,
    },
    waitingOnSupplier: {
      icon: TimerOutlined,
      variant: "warning",
      type: "default",
      name: statusText.waitingOnSupplier,
    },
    dealerPreparation: {
      icon: Assignment,
      variant: "warning",
      type: "default",
      name: statusText.dealerPreparation,
    },
    scrap: {
      icon: MonetizationOn,
      variant: "success",
      type: "default",
      name: statusText.scrap,
    },
  };

  // Add lowercase aliases so camelCase keys lowercased at runtime still resolve
  // (e.g. "inProcess".toLowerCase() === "inprocess" maps to the same entry as "inProcess")
  const withAliases: { [key: string]: IconType } = { ...base };
  Object.keys(base).forEach((key) => {
    withAliases[key.toLowerCase()] = base[key];
  });

  return withAliases;
};

export const getStatusTexts = (t: (key: string) => string): StatusTexts => ({
  needAction: t("returns.list.grid.status.needAction"),
  inProcess: t("returns.list.grid.status.inProcess"),
  readyToShip: t("returns.list.grid.status.readyToShip"),
  waitingForNFe: t("returns.list.grid.status.waitingForNFe"),
  waitingOnSupplier: t("returns.list.grid.status.waitingOnSupplier"),
  rejected: t("returns.list.grid.status.rejected"),
  completed: t("returns.list.grid.status.completed"),
  dealerPreparation: t("returns.list.grid.status.dealerPreparation"),
  scrap: t("returns.list.grid.status.scrap"),
});

export const getTypeOfPartsText = (
  t: (key: string) => string
): TypeOfPartsTexts => ({
  SMALL: t("returns.details.dealerPreparation.typeOfParts.small"),
  BULK: t("returns.details.dealerPreparation.typeOfParts.bulk"),
  UNKNOWN: t("returns.details.dealerPreparation.typeOfParts.unknown"),
});

export async function handleAPICall<T>(
  asyncCall: () => Promise<T>,
  {
    loadingStatus,
    onSuccess,
    onFailure,
  }: {
    loadingStatus: (isLoading: boolean) => void;
    onSuccess: (data: T) => void;
    onFailure: (e: any) => void;
  }
) {
  loadingStatus(true);
  try {
    const result = await asyncCall();
    onSuccess(result);
  } catch (e) {
    console.error(e);
    onFailure(e);
  } finally {
    loadingStatus(false);
  }
}

export function isAuthError(error: any): boolean {
  return (
    error?.response?.data?.statusCode === 401 ||
    error?.response?.status === 401 ||
    error?.response?.data?.statusCode === 403 ||
    error?.response?.status === 403
  );
}

export function formatAddress(addressType: AddressType): string {
  const addressFields = [
    addressType.name,
    addressType.street,
    addressType.city,
    addressType.postCode,
  ];
  const formattedAddress = addressFields
    .map((addressField) => addressField.trim())
    .filter((trimmedAddressField) => trimmedAddressField !== "")
    .map((nonBlankAddressField) => convertToCamelCase(nonBlankAddressField))
    .join(", ");
  return formattedAddress || "-";
}

export function formatLabelAddress(addressType: AddressType): string {
  // Region/country are kept as-is so codes like "IL"/"US" aren't camel-cased.
  const camelCasedFields = [
    addressType.name,
    addressType.street,
    addressType.city,
  ]
    .map((field) => (field ?? "").trim())
    .filter((field) => field !== "")
    .map((field) => convertToCamelCase(field));
  const verbatimFields = [
    addressType.postCode,
    addressType.region,
    addressType.country,
  ]
    .map((field) => (field ?? "").trim())
    .filter((field) => field !== "");
  const formatted = [...camelCasedFields, ...verbatimFields].join(", ");
  return formatted || "-";
}
